import { usePerformanceContext } from '@/components/optimizations/PerformanceProvider';

/**
 * Hook específico para otimizações de performance
 * Fornece acesso direto às configurações de otimização
 */
export const usePerformanceOptimizations = () => {
  const { optimizations, browserInfo, updateOptimizations } = usePerformanceContext();
  
  return {
    optimizations,
    browserInfo,
    updateOptimizations,
    
    // Helpers específicos
    shouldUseWebP: optimizations.useWebP,
    shouldUseAVIF: optimizations.useAVIF,
    shouldEagerLoad: optimizations.eagerLoading,
    shouldSimplifyAnimations: optimizations.simplifiedAnimations,
    shouldShowEscapeButton: optimizations.showEscapeButton,
    shouldOptimizeTouch: optimizations.optimizedTouch,
    shouldPreventZoom: optimizations.preventZoom,
    
    // Cache strategy
    cacheStrategy: optimizations.cacheStrategy,
    imageQuality: optimizations.imageQuality,
    
    // Browser checks
    isInstagram: browserInfo.isInstagram,
    isFacebook: browserInfo.isFacebook,
    isWebView: browserInfo.isWebView,
    isMobile: browserInfo.isMobile
  };
};

/**
 * Hook para estratégias de loading inteligente
 */
export const useIntelligentLoading = (priority: 'high' | 'medium' | 'low' = 'medium') => {
  const { optimizations, browserInfo } = usePerformanceContext();
  
  const getLoadingStrategy = () => {
    // High priority: sempre eager
    if (priority === 'high') {
      return {
        loading: 'eager' as const,
        fetchPriority: 'high' as const,
        preload: true,
        shouldLazyLoad: false
      };
    }
    
    // Instagram/WebView: estratégia específica
    if (browserInfo.isWebView) {
      return {
        loading: optimizations.eagerLoading ? 'eager' as const : 'lazy' as const,
        fetchPriority: priority === 'medium' ? 'high' as const : 'auto' as const,
        preload: priority === 'medium' && optimizations.preloadCritical,
        shouldLazyLoad: !optimizations.eagerLoading
      };
    }
    
    // Navegador padrão: lazy loading otimizado
    return {
      loading: 'lazy' as const,
      fetchPriority: 'auto' as const,
      preload: optimizations.preloadCritical,
      shouldLazyLoad: optimizations.lazyLoading
    };
  };
  
  return getLoadingStrategy();
};

/**
 * Hook para otimizações de imagem
 */
export const useImageOptimizations = () => {
  const { optimizations, browserInfo } = usePerformanceContext();
  
  const getImageFormat = (src: string) => {
    const baseUrl = src.replace(/\.(jpg|jpeg|png|webp|avif)$/, '');
    
    // Instagram: JPG otimizado
    if (browserInfo.isInstagram) {
      return {
        src: `${baseUrl}.jpg`,
        fallback: `${baseUrl}.jpg`,
        format: 'jpg' as const,
        quality: optimizations.imageQuality
      };
    }
    
    // WebView com suporte limitado
    if (browserInfo.isWebView && !optimizations.useAVIF) {
      return {
        src: `${baseUrl}.webp`,
        fallback: `${baseUrl}.jpg`,
        format: 'webp' as const,
        quality: optimizations.imageQuality
      };
    }
    
    // Navegador padrão: formatos modernos
    return {
      src: optimizations.useAVIF ? `${baseUrl}.avif` : `${baseUrl}.webp`,
      fallback: `${baseUrl}.jpg`,
      format: optimizations.useAVIF ? 'avif' as const : 'webp' as const,
      quality: optimizations.imageQuality
    };
  };
  
  const getResponsiveSources = (src: string) => {
    const baseUrl = src.replace(/\.(jpg|jpeg|png|webp|avif)$/, '');
    
    if (!optimizations.useResponsiveImages) {
      return [];
    }
    
    const sources = [];
    
    // AVIF sources (se suportado)
    if (optimizations.useAVIF && !browserInfo.isInstagram) {
      sources.push({
        media: '(min-width: 1280px)',
        srcSet: `${baseUrl}-lg.avif 1920w, ${baseUrl}-md.avif 1280w`,
        type: 'image/avif'
      });
      sources.push({
        media: '(min-width: 768px)',
        srcSet: `${baseUrl}-md.avif 1280w, ${baseUrl}-sm.avif 768w`,
        type: 'image/avif'
      });
      sources.push({
        srcSet: `${baseUrl}-sm.avif 768w`,
        type: 'image/avif'
      });
    }
    
    // WebP sources (se suportado)
    if (optimizations.useWebP && !browserInfo.isInstagram) {
      sources.push({
        media: '(min-width: 1280px)',
        srcSet: `${baseUrl}-lg.webp 1920w, ${baseUrl}-md.webp 1280w`,
        type: 'image/webp'
      });
      sources.push({
        media: '(min-width: 768px)',
        srcSet: `${baseUrl}-md.webp 1280w, ${baseUrl}-sm.webp 768w`,
        type: 'image/webp'
      });
      sources.push({
        srcSet: `${baseUrl}-sm.webp 768w`,
        type: 'image/webp'
      });
    }
    
    return sources;
  };
  
  return {
    getImageFormat,
    getResponsiveSources,
    shouldUseWebP: optimizations.useWebP,
    shouldUseAVIF: optimizations.useAVIF,
    shouldUseResponsive: optimizations.useResponsiveImages,
    imageQuality: optimizations.imageQuality
  };
};

/**
 * Hook para otimizações de animação
 */
export const useAnimationOptimizations = () => {
  const { optimizations } = usePerformanceContext();
  
  const getAnimationClasses = (baseClasses: string) => {
    if (optimizations.simplifiedAnimations) {
      // Remove animações complexas e usa versões simplificadas
      return baseClasses
        .replace(/animate-fade-in-up/g, 'animate-fade-in')
        .replace(/animate-slide-in-up/g, 'animate-fade-in')
        .replace(/duration-\d+/g, 'duration-200')
        .replace(/delay-\d+/g, '');
    }
    
    if (optimizations.reducedMotion) {
      // Remove todas as animações
      return baseClasses.replace(/animate-[\w-]+/g, '');
    }
    
    return baseClasses;
  };
  
  const getAnimationStyle = () => {
    if (optimizations.simplifiedAnimations) {
      return {
        animationDuration: '0.2s',
        animationTimingFunction: 'ease-out'
      };
    }
    
    if (optimizations.reducedMotion) {
      return {
        animationDuration: '0s',
        transition: 'none'
      };
    }
    
    return {};
  };
  
  return {
    getAnimationClasses,
    getAnimationStyle,
    shouldSimplify: optimizations.simplifiedAnimations,
    shouldReduce: optimizations.reducedMotion
  };
};

/**
 * Hook para otimizações de formulário
 */
export const useFormOptimizations = () => {
  const { optimizations, browserInfo } = usePerformanceContext();
  
  const getInputStyle = () => {
    if (optimizations.preventZoom && browserInfo.isMobile) {
      return {
        fontSize: '16px' // Previne zoom no iOS
      };
    }
    
    return {};
  };
  
  const getFormProps = () => {
    return {
      // Otimizações para touch
      style: optimizations.optimizedTouch ? {
        touchAction: 'manipulation'
      } : {},
      
      // Prevenir zoom
      autoComplete: optimizations.optimizedForms ? 'on' : 'off'
    };
  };
  
  return {
    getInputStyle,
    getFormProps,
    shouldOptimizeTouch: optimizations.optimizedTouch,
    shouldPreventZoom: optimizations.preventZoom,
    shouldShowEscape: optimizations.showEscapeButton
  };
};

/**
 * Hook para debug e desenvolvimento
 */
export const usePerformanceDebug = () => {
  const context = usePerformanceContext();
  
  const logOptimization = (component: string, optimization: string) => {
    if (context.debugMode) {
      console.log(`🔧 ${component}: ${optimization}`);
    }
  };
  
  const logImageLoad = (src: string, format: string, size?: number) => {
    if (context.debugMode) {
      console.log(`🖼️ Image loaded: ${src} (${format}${size ? `, ${Math.round(size/1024)}KB` : ''})`);
    }
  };
  
  const logPerformanceMetric = (metric: string, value: number) => {
    if (context.debugMode) {
      console.log(`📊 ${metric}: ${value}ms`);
    }
  };
  
  return {
    logOptimization,
    logImageLoad,
    logPerformanceMetric,
    debugMode: context.debugMode,
    toggleDebug: context.toggleDebug
  };
};

// Re-export do hook principal para compatibilidade
export { usePerformanceContext } from '@/components/optimizations/PerformanceProvider';
